import { useLifeAreaById } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { Plus, SettingsSort18 } from 'shared/assets/icons';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import { PageEvents } from 'shared/services/tracking';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { GoalsOverview } from './components/goals-overview';
import * as Styled from './life-area-detail.style';
import { OutletContext } from './outlet-context';

export const SummaryGoals: React.FC = () => {
  const { t } = useTranslation();
  useTrackPageVisitEvent(PageEvents.LifeAreaDetailGoals, undefined);

  const { id } = useParams();
  const { mainScrollRef } = useOutletContext<OutletContext>();
  const lifeArea = useLifeAreaById(id);
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const openCreateGoalDialog = useOpenCreateGoalDialog();

  const showCompletedGoals =
    user?.settings?.lifeAreaDetail?.goals?.showCompletedGoals ?? true;

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings({
      lifeAreaDetail: {
        goals: {
          showCompletedGoals: value,
        },
      },
    });
  };

  const onAddGoal = () => {
    if (!lifeArea) {
      return;
    }
    openCreateGoalDialog({ lifeAreaId: lifeArea.id });
  };

  const goalsUrl = useMemo(
    () => replaceUrlParams(Paths.LifeAreaDetailGoals, { id: id! }),
    [id],
  );

  if (!lifeArea) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.life-area-detail.sections.goals.label')}
      url={goalsUrl}
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton icon={Plus} onClick={onAddGoal} color={Colors.Default} />
          <IconButton
            icon={SettingsSort18}
            onClick={() => setIsSortMenuOpen(true)}
            color={Colors.Default}
          />
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={() => setIsSortMenuOpen(false)}
              position="bottom-end"
            >
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
      noPadding
    >
      <GoalsOverview
        lifeArea={lifeArea}
        hideCompleted={!showCompletedGoals}
        scrollElement={mainScrollRef}
        hideArchived
      />
    </PageSectionBase>
  );
};
